/**
 * Ciftora ziyaretçi analitiği — kendi altyapımız, üçüncü taraf yok, PII gönderilmez.
 * İzin yoksa kalıcı ziyaretçi kimliği üretilmez; yalnız oturum bazlı kimliksiz ölçüm gider.
 */
import { api } from "@/lib/api";

const CONSENT_KEY = "ciftora_cookie_consent";
const SESSION_KEY = "ciftora_session_id";
const VISITOR_KEY = "ciftora_visitor_id";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const API_PATH = "/analytics/collect";

let queue = [];
let timer = null;
let lastPath = null;
let lastPathAt = Date.now();

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
  } catch {
    return null;
  }
};

export const saveConsent = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, at: new Date().toISOString() }));
  if (!consent?.analytics) localStorage.removeItem(VISITOR_KEY);
  window.dispatchEvent(new CustomEvent("ciftora-consent-changed", { detail: consent }));
};

const rid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

const sessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = rid("s");
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

/** Kalıcı ziyaretçi kimliği yalnız analitik izni varsa oluşturulur. */
const visitorId = () => {
  if (!getConsent()?.analytics) return null;
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = rid("v");
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
};

export const captureUtm = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v.slice(0, 80);
  });
  if (!Object.keys(utm).length) return;
  if (!localStorage.getItem("ciftora_utm_first")) {
    localStorage.setItem("ciftora_utm_first", JSON.stringify(utm));
  }
  localStorage.setItem("ciftora_utm_last", JSON.stringify(utm));
};

const attribution = () => {
  try {
    return {
      first_touch: JSON.parse(localStorage.getItem("ciftora_utm_first") || "null"),
      last_touch: JSON.parse(localStorage.getItem("ciftora_utm_last") || "null"),
    };
  } catch {
    return {};
  }
};

const sessionUtm = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v.slice(0, 80);
  });
  if (Object.keys(utm).length) return utm;
  return attribution().last_touch || {};
};

const flush = (useBeacon = false) => {
  if (!queue.length) return;
  const body = { events: queue.slice(0, 50) };
  queue = [];
  const url = `${process.env.REACT_APP_BACKEND_URL}/api${API_PATH}`;
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: "application/json" }));
    return;
  }
  api.post(API_PATH, body).catch(() => {});
};

const enqueue = (event) => {
  queue.push({
    ...event,
    session_id: sessionId(),
    visitor_id: visitorId(),
    consent: !!getConsent()?.analytics,
    referrer: document.referrer || null,
    utm: sessionUtm(),
    platform: "WEB",
  });
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => flush(false), 1200);
  if (queue.length >= 20) flush(false);
};

/** Sayfa görüntüleme — önceki sayfada geçen süreyi de bildirir. */
export const trackPageView = (path, title) => {
  const now = Date.now();
  if (lastPath && lastPath !== path) {
    enqueue({ event: "PAGE_VIEW", path: lastPath, page_title: document.title,
      duration: Math.round((now - lastPathAt) / 1000) });
  }
  lastPath = path;
  lastPathAt = now;
  enqueue({ event: "PAGE_VIEW", path, page_title: title || document.title });
};

/** Menü / özellik tıklaması: NAV_MARKET, NAV_VETERINER ... */
export const trackNav = (key, properties = {}) =>
  enqueue({ event: `NAV_${key}`, path: window.location.pathname, properties });

/** Dönüşüm olayı: WHATSAPP_CLICK, SIGN_UP_COMPLETED ... */
export const trackEvent = (event, properties = {}, entityId = null) =>
  enqueue({ event, path: window.location.pathname, properties, entity_id: entityId });

/** Geriye dönük uyumluluk: eski track() çağrıları dönüşüm olayına eşlenir. */
const LEGACY = {
  landing_visit: "PAGE_VIEW", signup: "SIGN_UP_COMPLETED", login: "LOGIN",
  listing_view: "LISTING_VIEWED", listing_created: "LISTING_CREATED",
  farm_created: "FARM_CREATED", message_started: "LISTING_CONTACT_CLICK",
};
export const track = (event, props = {}) => {
  const mapped = LEGACY[event];
  if (mapped === "PAGE_VIEW") return;
  if (mapped) trackEvent(mapped, props);
};

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (lastPath) {
      enqueue({ event: "PAGE_VIEW", path: lastPath, page_title: document.title,
        duration: Math.round((Date.now() - lastPathAt) / 1000) });
    }
    flush(true);
  });
}
